<template>
    <Dialog
        v-model:visible="visible"
        :style="{ width: '56vw' }"
        header="Manage Department Manager"
        :modal="true"
        class="p-fluid ta-dialog"
        @hide="$emit('hide-dialog', true)"
    >
        <!-- Help banner -->
        <div class="ta-help mb-2">
            <i class="pi pi-info-circle" />
            <span>Appoint or remove users as department manager. A manager has oversight of the department's tasks and members.</span>
        </div>

        <!-- Current Managers Section -->
        <div class="ta-section ta-section--current">
            <div class="ta-section__head">
                <i class="pi pi-user" />
                <span class="ta-section__title">Current Managers</span>
                <span class="ta-badge">{{ currentManagers.length }}</span>
            </div>
            <div class="ta-section__body ta-section__body--flush">
                <div v-if="currentManagers.length === 0" class="p-3 text-center">
                    <span class="ta-empty">No manager assigned to this department yet.</span>
                </div>
                <div v-else class="ta-current-list">
                    <div
                        v-for="(mgr, idx) in currentManagers"
                        :key="mgr.id"
                        class="ta-current-row"
                        :class="{ 'ta-current-row--delegating': removingManagerId === mgr.user_id }"
                    >
                        <div class="ta-current-row__info">
                            <i class="pi pi-user" />
                            <span class="ta-current-row__name">
                                {{ mgr.fullname || `User #${mgr.user_id}` }}
                            </span>
                            <span v-if="idx === 0" class="ta-current-row__badge">Primary</span>
                        </div>
                        <div class="ta-current-row__actions">
                            <Button
                                v-if="authStore.hasPermission(UserPermission.APPOINT_AS_MANAGER)"
                                icon="pi pi-user-minus"
                                class="p-button-rounded p-button-text p-button-danger ta-current-row__btn"
                                :disabled="removingManagerId === mgr.user_id"
                                :loading="removingManagerId === mgr.user_id"
                                v-tooltip.left="`Remove ${mgr.fullname || 'this user'} as manager`"
                                @click="handleRemoveManager(mgr)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Appoint Manager Section -->
        <div
            v-if="authStore.hasPermission(UserPermission.APPOINT_AS_MANAGER)"
            class="ta-section ta-section--pending mt-3"
        >
            <div class="ta-section__head">
                <i class="pi pi-user-plus" />
                <span class="ta-section__title">Appoint a New Manager</span>
            </div>
            <div class="ta-section__body">
                <!-- Search bar -->
                <div class="ta-table-header">
                    <span class="p-input-icon-left ta-search w-full">
                        <i class="pi pi-search" />
                        <input
                            v-model="searchQuery"
                            type="text"
                            class="p-inputtext w-full"
                            placeholder="Search user by name..."
                            @input="onSearchInput"
                        />
                    </span>
                </div>

                <!-- Candidates table -->
                <div
                    v-if="searchQuery && searchQuery.length > 0"
                    class="ta-candidates mt-2"
                >
                    <DataTable
                        v-model:selection="selectedUser"
                        :value="filteredUsers"
                        selectionMode="single"
                        dataKey="id"
                        :loading="searchingUsers"
                        :paginator="filteredUsers.length > 10"
                        :rows="10"
                        :rowsPerPageOptions="[5, 10, 20]"
                        responsiveLayout="scroll"
                        paginatorTemplate="CurrentPageReport PrevPageLink PageLinks NextPageLink RowsPerPageDropdown"
                        currentPageReportTemplate="{first}–{last} of {totalRecords}"
                        class="p-datatable-sm"
                    >
                        <Column field="fullname" header="Name">
                            <template #body="{ data }">
                                <div class="flex align-items-center gap-2">
                                    <i class="pi pi-user" style="color: var(--text-color-secondary, #6c757d);" />
                                    <span>{{ data.fullname }}</span>
                                </div>
                            </template>
                        </Column>
                        <Column field="email" header="Email" />
                        <Column header="Department">
                            <template #body="{ data }">
                                <span
                                    v-if="userDepartments[data.id]?.length"
                                    :title="userDepartments[data.id].map(d => d.name).join(', ')"
                                >
                                    {{ userDepartments[data.id].map(d => d.name).join(', ') }}
                                </span>
                                <span v-else style="color: var(--text-color-secondary, #6c757d);">—</span>
                            </template>
                        </Column>
                    </DataTable>
                </div>

                <!-- Empty state when searching -->
                <div
                    v-if="searchQuery && searchQuery.length > 0 && !searchingUsers && filteredUsers.length === 0"
                    class="ta-empty p-2"
                >
                    No users found matching "{{ searchQuery }}".
                </div>

                <!-- Action button -->
                <div class="flex justify-content-end mt-2">
                    <Button
                        label="Appoint as Manager"
                        icon="pi pi-user-plus"
                        class="p-button-success p-button-sm"
                        style="width: auto;"
                        :disabled="!selectedUser || appointingManager"
                        :loading="appointingManager"
                        @click="handleAppointManager"
                    />
                </div>

                <small v-if="appointError" class="ta-inline-error">{{ appointError }}</small>
            </div>
        </div>

        <!-- Footer -->
        <template #footer>
            <div class="ta-footer">
                <div class="ta-summary">
                    <i class="pi pi-check-circle" />
                    <span>{{ currentManagers.length }} manager{{ currentManagers.length !== 1 ? 's' : '' }} assigned</span>
                </div>
                <div class="ta-footer__actions">
                    <Button
                        label="Close"
                        icon="pi pi-times"
                        class="p-button-outlined p-button-secondary"
                        @click="visible = false"
                    />
                </div>
            </div>
        </template>
    </Dialog>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { departmentStore, userStore, authStore } from '../../../../../store/global-stores';
import { UserPermission } from '../../../../../modules/permission/enum';
import { showToast } from '../../../../../utilities/toast-handler';
import { authApi } from '@/utilities/axios';

const props = defineProps({
    departmentId: {
        type: [Number, String],
        required: true,
    },
    managers: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['hide-dialog', 'refresh']);

const visible = ref(true);
const searchQuery = ref('');
const selectedUser = ref(null);
const filteredUsers = ref([]);
const searchingUsers = ref(false);
const appointingManager = ref(false);
const removingManagerId = ref(null);
const appointError = ref('');
const departmentUsers = ref([]);
const localManagers = ref([]);
const managerResolvedNames = ref({});
const userDepartments = ref({});

let searchTimer = null;

// Map manager user_ids to user objects from department users
const currentManagers = computed(() => {
    const managers = localManagers.value.length > 0 ? localManagers.value : (props.managers || []);
    if (managers.length === 0) return [];

    return managers.map((manager) => {
        // Try department users first, then managerResolvedNames
        const user = departmentUsers.value.find((u) => u.id === manager.user_id);
        const resolvedName = managerResolvedNames.value[manager.user_id];

        return {
            ...manager,
            fullname: user
                ? `${user.name.charAt(0).toUpperCase() + user.name.slice(1)} ${user.surname.charAt(0).toUpperCase() + user.surname.slice(1)}`
                : resolvedName || null,
        };
    });
});

// Refresh managers data from the API
const refreshManagers = async () => {
    try {
        const details = await departmentStore.fetchDepartmentDetails(props.departmentId);
        if (details?.managers) {
            localManagers.value = details.managers;
            // Resolve user names for each manager
            resolveManagerNames(details.managers);
        }
    } catch {
        // Silently fail
    }
};

// Resolve names for manager user_ids by fetching each user
const resolveManagerNames = async (managers) => {
    const userIds = managers.map((m) => m.user_id).filter((id) => id && !managerResolvedNames.value[id]);
    for (const userId of userIds) {
        try {
            const response = await authApi.get(`${process.env.VUE_APP_API_VERSION}/admin/users/${userId}`);
            if (response.statusCode === 200 && response.data) {
                const user = response.data;
                const fullname = `${user.name.charAt(0).toUpperCase() + user.name.slice(1)} ${user.surname.charAt(0).toUpperCase() + user.surname.slice(1)}`;
                managerResolvedNames.value[userId] = fullname;
            }
        } catch {
            // Skip individual failures
        }
    }
};

// Fetch department users on mount to resolve manager names
onMounted(async () => {
    try {
        await departmentStore.getDepartmentUsers(props.departmentId);
        departmentUsers.value = departmentStore.deptUsers;
        await refreshManagers();
    } catch {
        // Silently fail — names just won't resolve
    }
});

// Search users with debounce
const onSearchInput = () => {
    if (searchTimer) clearTimeout(searchTimer);

    const query = searchQuery.value?.trim();
    if (!query || query.length < 1) {
        filteredUsers.value = [];
        userDepartments.value = {};
        return;
    }

    searchTimer = setTimeout(async () => {
        try {
            searchingUsers.value = true;
            selectedUser.value = null;
            await userStore.fetchAllUsers({ name: query, page_size: 20 }, false, true);

            // Exclude users who are already managers
            const managerUserIds = currentManagers.value.map((m) => m.user_id);
            filteredUsers.value = (userStore.users || []).filter(
                (u) => !managerUserIds.includes(u.id)
            );

            // Fetch departments for filtered users in parallel
            const deptResults = await Promise.all(
                filteredUsers.value.map(async (u) => {
                    try {
                        const depts = await departmentStore.fetchUserDepartments(u.id);
                        return { userId: u.id, departments: depts || [] };
                    } catch {
                        return { userId: u.id, departments: [] };
                    }
                })
            );
            const deptMap = {};
            deptResults.forEach(({ userId, departments }) => {
                deptMap[userId] = departments;
            });
            userDepartments.value = deptMap;
        } catch {
            showToast({}, 'Failed to search users');
        } finally {
            searchingUsers.value = false;
        }
    }, 300);
};

// Appoint selected user as manager
const handleAppointManager = async () => {
    if (!selectedUser.value) return;

    try {
        appointingManager.value = true;
        appointError.value = '';
        const success = await departmentStore.appointManager(props.departmentId, selectedUser.value.id);
        if (success) {
            searchQuery.value = '';
            selectedUser.value = null;
            filteredUsers.value = [];
            await refreshManagers();
            await departmentStore.getDepartmentUsers(props.departmentId);
            departmentUsers.value = departmentStore.deptUsers;
            emit('refresh');
        }
    } catch {
        appointError.value = 'Failed to appoint manager';
    } finally {
        appointingManager.value = false;
    }
};

// Remove a manager
const handleRemoveManager = async (mgr) => {
    try {
        removingManagerId.value = mgr.user_id;
        const success = await departmentStore.removeManager(props.departmentId, mgr.user_id);
        if (success) {
            await refreshManagers();
            await departmentStore.getDepartmentUsers(props.departmentId);
            departmentUsers.value = departmentStore.deptUsers;
            emit('refresh');
        }
    } catch {
        showToast({}, 'Failed to remove manager');
    } finally {
        removingManagerId.value = null;
    }
};
</script>
